import React, { useState, useEffect, useReducer } from "react";
import {
  Paper,
  Grid,
  FormControl,
  Button,
  Typography,
  TextField,
  InputLabel,
  MenuItem,
  Select,
  createTheme,
  ThemeProvider,
  Skeleton,
  Theme,
  useMediaQuery,
} from "@mui/material";
import { useStyles } from "../common/appStyles";
import { useSelector } from "react-redux";
import { useDispatch } from 'react-redux';
import { RootState } from "../store/store";
import { showSnackbar } from "../services/snackbar.service";
import { GetAllGrades, GetUser, UpdateUserProfile, ChangeProfilePassword } from "../services/user-service";
import { IGrade } from "../types/manage";
import { IUserDetail } from "../types/reduxStore";
import { updateUserResponse } from "../store/userResponseSlice";
import { indexedDBService } from "../services/indexdb-service";
import { LoadingDialog } from "../components/HelperComponents/LoadingDialog";
import { ConfirmProfile } from "../components/HelperComponents/ConfirmProfile";
import { RoleEnum } from "../types/enums";
import { BannerAd } from "../components/main-layout/BannerAd";

function reducer(state, action) {
  switch (action.type) {
    case 'SET_FIELD':
      return { ...state, [action.field]: action.value };
    default:
      throw new Error();
  }
}

function isValidEmail(email) {
  const re = /^(([^<>()[\]\\.,;:\s@"$#^%&*]+(\.[^<>()[\]\\.,;:\s@"$#^%&*]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z0-9\-]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(String(email).toLowerCase());
}

/**
 * @remarks This page allows the user to update their own information. The information
 * they are able to change on the form is dependent on their role. (for example a System Administrator
 * Viewing this page would not be able to change their school or grade because they do not have a school
 * or grade.) Upon updating it will open a confirm dialog. {@link ConfirmProfile}
 * @returns - html form for updating a user's own information. 
 */
export function MyProfile() {

  const user = useSelector((state: RootState) => state.userDetail);
  const classes = useStyles();
  const dispatcher = useDispatch();

  const [isLoading, setIsLoading] = useState(false);
  const [grades, setGrades] = useState<IGrade[]>([]);
  const [showConfirm, setShowConfirm] = useState(false);
  const lgUp = useMediaQuery((theme: Theme) => theme.breakpoints.up('lg'));

  const [state, dispatch] = useReducer(reducer, {
    firstName: user.firstName,
    lastName: user.lastName,
    email: user.email,
    grade: user.grade,
    school: user.school,
  });

  const { firstName, lastName, email, grade, school } = state;

  const setField = (field, value) => {
    dispatch({ type: 'SET_FIELD', field, value });
  };

  const theme = createTheme({
    palette: {
      primary: {
        main: '#10AD61'
      },
      secondary: {
        main: '#10AD61'
      },
      background: {
        default: '#ffffff'
      },
    },
    typography: {
      h6: {
        color: '#484A57'
      },
      body1: {
        color: 'black'
      }
    },
  });

  const getGrades = () => {
    GetAllGrades()
      .then(response => {
        setGrades(response);
      })
      .catch((error) => {
        showSnackbar('Error geting data', 'error', 5000);
      });
  }

  useEffect(() => {
    getGrades();
    setField('firstName', user.firstName);
    setField('lastName', user.lastName);
    setField('email', user.email);
    setField('grade', user.grade);
    setField('school', user.school);
  }, [user]);

  const handleConfirm = () => {
    setShowConfirm(true);
  }

  const handleClose = () => {
    setShowConfirm(false);
  }

  const handleSubmit = (event: any) => {
    setShowConfirm(false);
    const updatedUser = {
      "id": user["id"],
      firstName,
      lastName,
      email,
      grade,
      "roleId": user["roleId"],
      "role": user["role"],
      "schoolId": user["schoolId"],
      "school": user["school"]
    }
    handleSave(updatedUser);
  };

  const handleSave = async (savedUser) => {
    setIsLoading(true);
    try {
      await UpdateUserProfile(savedUser);
      localStorage.setItem('email', email);
      updateInfo();
      showSnackbar('Saved Successfully', 'success', 5000);
    } catch (error) {
      showSnackbar('Error saving data', 'error', 5000);
    }
    finally {
      setIsLoading(false);
    }
  }

  const updateInfo = async () => {
    try {
      const userResp: IUserDetail = await GetUser();
      localStorage.setItem("user", btoa(JSON.stringify(userResp)));
      dispatcher(updateUserResponse(userResp));
      await indexedDBService.setItem('User', 'userResp', userResp);
    } catch (error) {
      console.error('Error in updateInfo:', error);
    }
  };

  const handleResetPassword = async (event: any) => {
    event.preventDefault();
    if (email === user.email) {
      try {
        await ChangeProfilePassword();
        showSnackbar('Reset Password Email Sent', 'success', 5000);
      }
      catch (error) {
        showSnackbar('Error sending email', 'error', 5000);
      }
    }
    else {
      showSnackbar('save email changes first', 'warning', 5000)
    }
  }

  const isChanged = !
    (
      ((firstName === user.firstName)
        && (lastName === user.lastName)
        && (email === user.email)
        && (grade === user.grade)
        && (school === user.school))
      ||
      ((firstName === "")
        || (lastName === "")
        || (email === "")
        || (grade === "")
        || (school === "")
        || (isLoading === true)
      )
    );

  const isValid =
    (
      isValidEmail(email)
    )

  return (
    <div>
      <ThemeProvider theme={theme}>
        <Grid container className={classes.wrapper} justifyContent={"center"} alignItems={"center"} >
          {user.roleId >= RoleEnum.CredentialingManager && !lgUp && <Grid className={classes.bannerTopPadding}>
            <BannerAd />
          </Grid>}
          <Grid item xs={12} className={classes.itemPadding}>
            <Grid container direction={"column"} alignItems={"center"} rowSpacing={4} className={classes.subWrapper3} >
              <Grid item>
                <Typography sx={{ flex: 1 }} variant="h6" component="div">
                  My Profile
                </Typography>
              </Grid>
              {!user.email ? <Grid item>
                <Skeleton variant="rounded" width="40vh" height="30vh" />
              </Grid>
                :
                <Grid item className={classes.profileGrid}>
                  <Paper elevation={3}>
                    <Grid container direction={"column"} className={classes.gridPadding} rowSpacing={'2vh'}>
                      <Grid item>
                        <FormControl fullWidth>
                          <TextField
                            disabled={user.roleId > RoleEnum.Judge ? true : false}
                            label="First Name"
                            value={firstName}
                            onChange={(event) => setField('firstName', event.target.value)}
                          />
                        </FormControl>
                      </Grid>
                      <Grid item>
                        <FormControl fullWidth>
                          <TextField
                            disabled={user.roleId > RoleEnum.Judge ? true : false}
                            label="Last Name"
                            value={lastName}
                            onChange={(event) => setField('lastName', event.target.value)}
                          />
                        </FormControl>
                      </Grid>
                      <Grid item>
                        <FormControl fullWidth>
                          <TextField
                            disabled={user.roleId > RoleEnum.Judge ? true : false}
                            label="Email"
                            value={email}
                            onChange={(event) => setField('email', event.target.value)}
                          />
                        </FormControl>
                      </Grid>
                      {user.roleId === RoleEnum.Youth &&
                        <Grid item className={classes.selectTopBotPadding}>
                          <FormControl fullWidth>
                            <InputLabel>Grade</InputLabel>
                            <Select
                              label="Grade"
                              MenuProps={{
                                disableScrollLock: true,
                                PaperProps: {
                                  style: {
                                    maxHeight: "15vh",
                                  },
                                }
                              }}
                              value={grade}
                              onChange={(event) => setField('grade', event.target.value)}
                            >
                              {grades.map((grade) => (
                                <MenuItem value={grade.id}>
                                  {grade.name}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>}
                      {user.roleId >= RoleEnum.SchoolCoordinator &&
                        <Grid item>
                          <FormControl fullWidth>
                            <TextField
                              disabled
                              label="School"
                              value={school}
                              onChange={(event) => setField('school', event.target.value)}
                            />
                          </FormControl>
                        </Grid>}
                    </Grid>
                  </Paper>
                </Grid>}
              <Grid item>
                <Grid container columnSpacing={3} alignItems={"center"} justifyContent={"center"}>
                  <Grid item>
                    {!user.email ?
                      <Skeleton variant="rounded" width="22vh" height="5vh" />
                      :
                      <Button variant="contained" onClick={handleConfirm} disabled={(isChanged && isValid) ? false : true}>
                        Update Profile
                      </Button>
                    }
                  </Grid>
                  <Grid item>
                    {!user.email ?
                      <Skeleton variant="rounded" width="25vh" height="5vh" />
                      :
                      <Button variant="outlined" onClick={handleResetPassword} >
                        reset my password
                      </Button>
                    }
                  </Grid>
                </Grid>
              </Grid>
              {isLoading && <LoadingDialog
                loading={isLoading}
              />}
              {showConfirm && <ConfirmProfile
                submit={handleSubmit}
                close={handleClose}
                showConfirm={showConfirm}
              />
              }
            </Grid>
          </Grid>
        </Grid>
      </ThemeProvider>
    </div >
  );
}
